import React from 'react';

/**
 * Microphone icon for `ParticipantTile`'s control row (`SF-05D-TILE-CONTROLS`).
 *
 * Inline `<svg>` on `currentColor`, explicitly **not** an emoji (design § D-E, task 1.3): emoji are
 * platform bitmaps that fall back blank offline, whereas `currentColor` lets antd's `danger` state
 * recolour the icon for free when the device has failed.
 *
 * The `off` state **adds a slash line** rather than swapping to a second picture, so the muted and
 * unmuted glyphs cannot drift apart. The mockup expresses that as
 * `.ic[data-off="no"] .ic-slash { display: none }`; the slash is rendered conditionally here as
 * well, so the state is observable in jsdom — where CSS-module rules never apply — rather than only
 * to the eye. `data-off` is still emitted so the mockup's own selector keeps working verbatim.
 *
 * Geometry is pinned by the task list 1.2: viewBox `0 0 24 24`, stroke width 1.9, round caps and joins.
 *
 * @component
 * @param {Object} props
 * @param {boolean?} props.off render the muted (slashed) variant
 * @returns {JSX.Element} SVG icon
 */
const IconMic = props => {
  const { off = false } = props;

  return (
    <span className="ic" data-off={off ? 'yes' : 'no'} style={WRAPPER_STYLE}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="9.25" y="2.75" width="5.5" height="10" rx="2.75" fill="currentColor" stroke="none" />
        <path d="M5.5 11v1a6.5 6.5 0 0 0 13 0v-1" />
        <line x1="12" y1="18.5" x2="12" y2="21.5" />
        <line x1="8.5" y1="21.5" x2="15.5" y2="21.5" />
        {off ? <line className="ic-slash" x1="4" y1="4" x2="20" y2="20" /> : null}
      </svg>
    </span>
  );
};

/** The whole of the `.ic` wrapper rule — too small to earn a `.module.css` of its own (§ D-E). */
const WRAPPER_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
};

export default IconMic;
